import { useMutation } from '@tanstack/vue-query'

import type { AanFeatureListResponse, MutationResponse, TriggerRulesData } from '@/services/api/aan.api'
import type { ApiResponse } from '@/types/user'

import { useAxios } from '@/composables/use-axios'
import { aanFeatureListApi, aanSaveConfigApi } from '@/services/api/aan.api'

/**
 * API配置 - 基于老项目的URL配置
 */
const { axiosInstance } = useAxios()

// API完整域名配置
const API_BASE_URL_V2_1 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/`
const API_BASE_URL_V2_2 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-2/`

/**
 * Onboarding 问卷答案保存请求数据
 */
interface OnboardingAnswerSaveRequest {
  answer: object
}

/**
 * Onboarding 问卷答案保存响应数据
 */
interface OnboardingAnswerSaveResponse {
  success?: boolean
  [key: string]: any
}

/**
 * 任务完成信息项
 */
interface OnboardingTaskCompleteItem {
  task_id: number
  is_finished: boolean
}

/**
 * 任务完成信息响应数据
 */
interface OnboardingTaskCompleteInfoResponse {
  task_info: OnboardingTaskCompleteItem[]
  all_task_info: boolean
}

/**
 * 任务完成请求数据
 */
interface OnboardingTaskCompleteRequest {
  task_id: number
}

/**
 * 任务完成响应数据
 */
interface OnboardingTaskCompleteResponse {
  code: number
  message: string
  data: null
}

/**
 * 任务初始化请求数据
 */
interface OnboardingTaskInitRequest {
  task_ids: string[]
}

/**
 * 任务初始化响应数据
 */
interface OnboardingTaskInitResponse {
  code: number
  message: string
  data: null
}

/**
 * 全部任务完成响应数据
 */
interface OnboardingTaskAllCompleteResponse {
  code: number
  message: string
  data: null
}

/**
 * 所有权转移请求数据
 */
interface OwnershipTransferRequest {
  user_id: number
}

/**
 * 所有权转移响应数据
 */
interface OwnershipTransferResponse {
  code: number
  message: string
}

/**
 * 批准成员请求数据
 */
interface ApproveMemberRequest {
  user_id: number
}

/**
 * 批准成员响应数据
 */
interface ApproveMemberResponse {
  code: number
  message: string
}

// ==================== 新版 Onboarding Progress API 类型定义 ====================

/**
 * Onboarding Progress 数据结构
 * progress_data 是一个灵活的 JSON 对象，可以存储任意数据
 */
export interface OnboardingProgressData {
  version: string
  current_step: number
  current_platform: string
  progress_data: Record<string, any> | null
  is_completed: boolean
  completed_at: string
}

/**
 * 获取 Onboarding Progress 请求
 */
interface OnboardingProgressGetRequest {
  version?: string
}

/**
 * 获取 Onboarding Progress 响应
 */
type OnboardingProgressGetResponse = OnboardingProgressData | null

/**
 * 保存 Onboarding Progress 请求
 */
interface OnboardingProgressSaveRequest {
  version?: string
  current_step: number
  current_platform?: string
  progress_data?: Record<string, any>
  is_completed?: boolean
}

/**
 * 保存 Onboarding Progress 响应
 */
interface OnboardingProgressSaveResponse {
  success: boolean
}

/**
 * 完成 Onboarding Progress 请求
 */
interface OnboardingProgressCompleteRequest {
  version?: string
}

/**
 * 完成 Onboarding Progress 响应
 */
interface OnboardingProgressCompleteResponse {
  success: boolean
}

/**
 * 重置 Onboarding Progress 请求
 */
interface OnboardingProgressResetRequest {
  version?: string
}

/**
 * 重置 Onboarding Progress 响应
 */
interface OnboardingProgressResetResponse {
  success: boolean
}

/**
 * 保存 Onboarding 问卷答案 - Vue Query Hook 版本（用于组件）
 */
export function useOnboardingAnswerSave() {
  return useMutation({
    mutationKey: ['onboardingAnswerSave'],
    mutationFn: async (data: OnboardingAnswerSaveRequest): Promise<OnboardingAnswerSaveResponse> => {
      const response = await axiosInstance.post<ApiResponse<OnboardingAnswerSaveResponse>>(
        `${API_BASE_URL_V2_1}onboarding-question/answer/save`,
        data,
      )
      return response.data?.data
    },
  })
}

/**
 * 保存 Onboarding 问卷答案 - 普通函数版本（用于 Pinia store）
 */
export async function onboardingAnswerSaveApi(data: OnboardingAnswerSaveRequest): Promise<OnboardingAnswerSaveResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingAnswerSaveResponse>>(
    `${API_BASE_URL_V2_1}onboarding-question/answer/save`,
    data,
  )
  return response.data?.data
}

/**
 * 获取 V7 Onboarding 任务完成信息 - Vue Query Hook 版本（用于组件）
 */
export function useV7OnboardingTaskCompleteInfo() {
  return useMutation({
    mutationKey: ['v7OnboardingTaskCompleteInfo'],
    mutationFn: async (): Promise<OnboardingTaskCompleteInfoResponse> => {
      const response = await axiosInstance.post<ApiResponse<OnboardingTaskCompleteInfoResponse>>(
        `${API_BASE_URL_V2_1}v7-onboarding-task/complete/info`,
      )
      return response.data?.data || { task_info: [], all_task_info: false }
    },
  })
}

/**
 * 获取 V7 Onboarding 任务完成信息 - 普通函数版本（用于 Pinia store）
 */
export async function v7OnboardingTaskCompleteInfoApi(): Promise<OnboardingTaskCompleteInfoResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingTaskCompleteInfoResponse>>(
    `${API_BASE_URL_V2_1}v7-onboarding-task/complete/info`,
  )
  return response.data?.data || { task_info: [], all_task_info: false }
}

export async function fetchStorePlatformInfo(): Promise<{ platform: string, shop?: string }> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post(`${API_BASE_URL_V2_1}attribuly/init`)
  const team = (response?.data?.data?.team) || {}
  return {
    platform: team.shop_type || '',
    shop: team.shop || team.web_url || '',
  }
}

/**
 * Recapture Free 状态检查响应数据
 */
export interface RecaptureFreeCheckData {
  recapture_revenue: number
  recapture_free: boolean
}

/**
 * 检查 Recapture Free 状态 - POST /v2-1/attribuly/recapture-free
 */
export async function recaptureFreeCheckApi(): Promise<ApiResponse<RecaptureFreeCheckData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<RecaptureFreeCheckData>>(
    `${API_BASE_URL_V2_1}attribuly/recapture-free`,
  )
  return response.data
}

/**
 * 重新订阅 Brevo 邮件 - POST /v2-1/attribuly/re-unblock-brevo-email
 */
export async function reUnblockBrevoEmailApi(email: string): Promise<ApiResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse>(
    `${API_BASE_URL_V2_1}attribuly/re-unblock-brevo-email`,
    { email },
  )
  return response.data
}

/**
 * 完成 V7 Onboarding 任务 - Vue Query Hook 版本（用于组件）
 */
export function useV7OnboardingTaskComplete() {
  return useMutation({
    mutationKey: ['v7OnboardingTaskComplete'],
    mutationFn: async (data: OnboardingTaskCompleteRequest): Promise<OnboardingTaskCompleteResponse> => {
      const response = await axiosInstance.post<OnboardingTaskCompleteResponse>(
        `${API_BASE_URL_V2_1}v7-onboarding-task/complete`,
        data,
      )
      return response.data
    },
  })
}

/**
 * 完成 V7 Onboarding 任务 - 普通函数版本（用于 Pinia store）
 */
export async function v7OnboardingTaskCompleteApi(data: OnboardingTaskCompleteRequest): Promise<OnboardingTaskCompleteResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<OnboardingTaskCompleteResponse>(
    `${API_BASE_URL_V2_1}v7-onboarding-task/complete`,
    data,
  )
  return response.data
}

/**
 * 初始化 V7 Onboarding 任务 - Vue Query Hook 版本（用于组件）
 */
export function useV7OnboardingTaskInit() {
  return useMutation({
    mutationKey: ['v7OnboardingTaskInit'],
    mutationFn: async (data: OnboardingTaskInitRequest): Promise<OnboardingTaskInitResponse> => {
      const response = await axiosInstance.post<OnboardingTaskInitResponse>(
        `${API_BASE_URL_V2_1}v7-onboarding-task/init`,
        data,
      )
      return response.data
    },
  })
}

/**
 * 初始化 V7 Onboarding 任务 - 普通函数版本（用于 Pinia store）
 */
export async function v7OnboardingTaskInitApi(data: OnboardingTaskInitRequest): Promise<OnboardingTaskInitResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<OnboardingTaskInitResponse>(
    `${API_BASE_URL_V2_1}v7-onboarding-task/init`,
    data,
  )
  return response.data
}

/**
 * 完成全部 V7 Onboarding 任务 - Vue Query Hook 版本（用于组件）
 */
export function useV7OnboardingTaskAllComplete() {
  return useMutation({
    mutationKey: ['v7OnboardingTaskAllComplete'],
    mutationFn: async (): Promise<OnboardingTaskAllCompleteResponse> => {
      const response = await axiosInstance.post<OnboardingTaskAllCompleteResponse>(
        `${API_BASE_URL_V2_1}v7-onboarding-task/all/complete`,
      )
      return response.data
    },
  })
}

/**
 * 完成全部 V7 Onboarding 任务 - 普通函数版本（用于 Pinia store）
 */
export async function v7OnboardingTaskAllCompleteApi(): Promise<OnboardingTaskAllCompleteResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<OnboardingTaskAllCompleteResponse>(
    `${API_BASE_URL_V2_1}v7-onboarding-task/all/complete`,
  )
  return response.data
}

/**
 * 转移所有权 - Vue Query Hook 版本（用于组件）
 */
export function useOwnershipTransfer() {
  return useMutation({
    mutationKey: ['ownershipTransfer'],
    mutationFn: async (data: OwnershipTransferRequest): Promise<OwnershipTransferResponse> => {
      const response = await axiosInstance.post<OwnershipTransferResponse>(
        `${API_BASE_URL_V2_1}attribuly/auth/update/ownership`,
        data,
      )
      return response.data
    },
  })
}

/**
 * 转移所有权 - 普通函数版本（用于 Pinia store）
 */
export async function ownershipTransferApi(data: OwnershipTransferRequest): Promise<OwnershipTransferResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<OwnershipTransferResponse>(
    `${API_BASE_URL_V2_1}attribuly/auth/update/ownership`,
    data,
  )
  return response.data
}

/**
 * 批准成员加入 - Vue Query Hook 版本（用于组件）
 */
export function useApproveMember() {
  return useMutation({
    mutationKey: ['approveMember'],
    mutationFn: async (data: ApproveMemberRequest): Promise<ApproveMemberResponse> => {
      const response = await axiosInstance.post<ApproveMemberResponse>(
        `${API_BASE_URL_V2_1}attribuly/auth/accept`,
        data,
      )
      return response.data
    },
  })
}

/**
 * 批准成员加入 - 普通函数版本（用于 Pinia store）
 */
export async function approveMemberApi(data: ApproveMemberRequest): Promise<ApproveMemberResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApproveMemberResponse>(
    `${API_BASE_URL_V2_1}attribuly/auth/accept`,
    data,
  )
  return response.data
}

// ==================== 新版 Onboarding Progress API ====================

/**
 * 获取 Onboarding Progress - 普通函数版本（用于 Pinia store）
 * 返回 null 表示不存在记录（需要走老版本或新建）
 */
export async function onboardingProgressGetApi(data?: OnboardingProgressGetRequest): Promise<OnboardingProgressGetResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingProgressGetResponse>>(
    `${API_BASE_URL_V2_1}onboarding/progress/get`,
    data || { version: 'v1' },
  )
  return response.data?.data || null
}

/**
 * 保存 Onboarding Progress - 普通函数版本（用于 Pinia store）
 * 支持深度合并 progress_data
 */
export async function onboardingProgressSaveApi(data: OnboardingProgressSaveRequest): Promise<OnboardingProgressSaveResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingProgressSaveResponse>>(
    `${API_BASE_URL_V2_1}onboarding/progress/save`,
    data,
  )
  return response.data?.data || { success: false }
}

/**
 * 标记 Onboarding 为已完成 - 普通函数版本（用于 Pinia store）
 */
export async function onboardingProgressCompleteApi(data?: OnboardingProgressCompleteRequest): Promise<OnboardingProgressCompleteResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingProgressCompleteResponse>>(
    `${API_BASE_URL_V2_1}onboarding/progress/complete`,
    data || { version: 'v1' },
  )
  return response.data?.data || { success: false }
}

/**
 * 重置 Onboarding Progress - 普通函数版本（用于 Pinia store）
 */
export async function onboardingProgressResetApi(data?: OnboardingProgressResetRequest): Promise<OnboardingProgressResetResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingProgressResetResponse>>(
    `${API_BASE_URL_V2_1}onboarding/progress/reset`,
    data || { version: 'v1' },
  )
  return response.data?.data || { success: false }
}

// ==================== Capture First Onboarding Frontend Contracts ====================

export type CaptureOnboardingGoal = 'maximize_revenue' | 'balance_revenue_and_coverage' | 'maximize_leads'
export type CaptureOnboardingOrderValueBand = 'high_aov' | 'standard_aov'
export type CaptureOnboardingAudience = 'cart_abandoners' | 'engaged_visitors' | 'all_visitors'
export type CaptureOnboardingStrategyKey
  = | 'cart_abandoner_recovery'
    | 'high_intent_visitor_capture'
    | 'engaged_visitor_capture'
    | 'broad_visitor_capture'

export interface CaptureOnboardingAnswers {
  goal: CaptureOnboardingGoal | null
  orderValueBand: CaptureOnboardingOrderValueBand | null
  audience: CaptureOnboardingAudience | null
}

export interface CaptureRuleCondition {
  field: string
  operator: string
  target: number
}

export interface CaptureOnboardingRecommendation {
  strategy_key: CaptureOnboardingStrategyKey
  goal: CaptureOnboardingGoal
  orderValueBand: CaptureOnboardingOrderValueBand
  audience: CaptureOnboardingAudience
  trigger_rules: TriggerRulesData
  trigger_rules_json: string
  preview_conditions: CaptureRuleCondition[]
}

export interface CaptureOnboardingStatusData {
  needs_onboarding: boolean
  has_trigger_rules: boolean
  is_activated: boolean
  aan_status: string
  max_emails: number
  trigger_rules: TriggerRulesData | null
}

export interface CaptureOnboardingActivateRequest {
  recommendation: CaptureOnboardingRecommendation
  max_emails?: number
}

export interface CaptureOnboardingActivateData {
  success: boolean
  strategy_key: CaptureOnboardingStrategyKey
  activated_at: string
}

export type CaptureOnboardingStatusResponse = MutationResponse<CaptureOnboardingStatusData>
export type CaptureOnboardingRecommendResponse = MutationResponse<CaptureOnboardingRecommendation>
export type CaptureOnboardingActivateResponse = MutationResponse<CaptureOnboardingActivateData>

function normalizeTriggerRules(rawTriggerRules: AanFeatureListResponse['trigger_rules']): TriggerRulesData | null {
  if (!rawTriggerRules) {
    return null
  }

  if (typeof rawTriggerRules === 'string') {
    try {
      return JSON.parse(rawTriggerRules) as TriggerRulesData
    }
    catch {
      return null
    }
  }

  return rawTriggerRules as TriggerRulesData
}

function hasNestedConditions(value: unknown): boolean {
  if (!value || typeof value !== 'object') {
    return false
  }

  const group = value as Record<string, unknown>
  const allOf = Array.isArray(group.all_of) ? group.all_of : []
  const anyOf = Array.isArray(group.any_of) ? group.any_of : []

  const allOfHasItems = allOf.some((item) => {
    if (!item || typeof item !== 'object') {
      return false
    }

    return 'field' in (item as Record<string, unknown>) || hasNestedConditions(item)
  })

  const anyOfHasItems = anyOf.some((item) => {
    if (!item || typeof item !== 'object') {
      return false
    }

    return 'field' in (item as Record<string, unknown>) || hasNestedConditions(item)
  })

  return allOfHasItems || anyOfHasItems
}

export function hasEffectiveCaptureActivation(config: Pick<AanFeatureListResponse, 'aan_status' | 'trigger_rules'> | null | undefined) {
  const triggerRules = normalizeTriggerRules(config?.trigger_rules ?? null)
  return String(config?.aan_status || '').toLowerCase() === 'open' && hasNestedConditions(triggerRules)
}

function serializeTriggerRules(triggerRules: TriggerRulesData) {
  return JSON.stringify(triggerRules)
}

export function buildCaptureOnboardingRecommendation(answers: CaptureOnboardingAnswers): CaptureOnboardingRecommendation {
  const goal = answers.goal || 'balance_revenue_and_coverage'
  const orderValueBand = answers.orderValueBand || 'standard_aov'
  const audience = answers.audience || 'engaged_visitors'

  let strategyKey: CaptureOnboardingStrategyKey = 'engaged_visitor_capture'
  let previewConditions: CaptureRuleCondition[] = []
  let triggerRules: TriggerRulesData = { all_of: [] }

  if (audience === 'cart_abandoners') {
    strategyKey = 'cart_abandoner_recovery'
    previewConditions = [
      { field: 'product_added_to_cart', operator: '>=', target: 1 },
      { field: 'stay_time', operator: '>=', target: orderValueBand === 'high_aov' ? 45 : 30 },
    ]
    triggerRules = { all_of: previewConditions }
  }
  else if (audience === 'engaged_visitors' && goal === 'maximize_revenue') {
    strategyKey = 'high_intent_visitor_capture'
    previewConditions = [
      { field: 'product_viewed', operator: '>=', target: 2 },
      { field: 'stay_time', operator: '>=', target: orderValueBand === 'high_aov' ? 60 : 45 },
    ]
    triggerRules = { all_of: previewConditions }
  }
  else if (audience === 'all_visitors') {
    strategyKey = 'broad_visitor_capture'
    previewConditions = [
      { field: 'product_viewed', operator: '>=', target: 2 },
      { field: 'collection_viewed', operator: '>=', target: 2 },
      { field: 'stay_time', operator: '>=', target: orderValueBand === 'high_aov' ? 75 : 60 },
    ]
    triggerRules = { any_of: previewConditions }
  }
  else {
    strategyKey = 'engaged_visitor_capture'
    previewConditions = [
      { field: 'product_viewed', operator: '>=', target: goal === 'maximize_leads' ? 1 : 2 },
      { field: 'stay_time', operator: '>=', target: orderValueBand === 'high_aov' ? 45 : 35 },
    ]
    triggerRules = { all_of: previewConditions }
  }

  return {
    strategy_key: strategyKey,
    goal,
    orderValueBand,
    audience,
    trigger_rules: triggerRules,
    trigger_rules_json: serializeTriggerRules(triggerRules),
    preview_conditions: previewConditions,
  }
}

export async function captureOnboardingStatusApi(): Promise<CaptureOnboardingStatusResponse> {
  const featureList = await aanFeatureListApi()
  const normalizedTriggerRules = normalizeTriggerRules(featureList?.trigger_rules ?? null)
  const isActivated = hasEffectiveCaptureActivation(featureList)

  return {
    code: 1,
    message: 'ok',
    data: {
      needs_onboarding: !isActivated,
      has_trigger_rules: hasNestedConditions(normalizedTriggerRules),
      is_activated: isActivated,
      aan_status: featureList?.aan_status || 'close',
      max_emails: featureList?.max_emails || 0,
      trigger_rules: normalizedTriggerRules,
    },
  }
}

export async function captureOnboardingRecommendApi(answers: CaptureOnboardingAnswers): Promise<CaptureOnboardingRecommendResponse> {
  return {
    code: 1,
    message: 'ok',
    data: buildCaptureOnboardingRecommendation(answers),
  }
}

export async function captureOnboardingActivateApi(data: CaptureOnboardingActivateRequest): Promise<CaptureOnboardingActivateResponse> {
  const response = await aanSaveConfigApi({
    config: {
      aan_status: 'open',
      max_emails: data.max_emails || 0,
      trigger_rules: data.recommendation.trigger_rules_json,
    },
  })

  return {
    code: response.code,
    message: response.message,
    data: response.code > 0
      ? {
          success: true,
          strategy_key: data.recommendation.strategy_key,
          activated_at: new Date().toISOString(),
        }
      : undefined,
  }
}
